import { Grid } from '@mui/material';
import { useState } from 'react';
import { useParams, useNavigate, Link, useLoaderData } from 'react-router';

import Auth from '../../../../api/auth';
import BreadCrumb from '../../../../components/BreadCrumb';
import DeleteFormModal from '../../../../components/DeleteFormModal/DeleteFormModal';
import OverviewTable from '../../../../components/OverviewTable/OverviewTable';
import useApiQuery from '../../../../hooks/useApiQuery';
import rbacCheck from '../../../../rbac/rbacCheck';

import EditProjectFormModal from './EditProjectFormModal';
import Products from './Products';

import { useUserData } from '@/redux/selectors';
import type { APIHookArg } from '@/types';
import type { Project as ProjectDetails } from '@/types/projectTypes';

interface ProjectInfoItem {
  title: string;
  value?: string;
  component?: React.ReactNode;
}

const Project = (): React.ReactElement => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const userData = useUserData();

  const loaderProject: ProjectDetails = useLoaderData();
  const [projectDetails, setProjectDetails] = useState<ProjectDetails>(loaderProject);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [editModalOpened, setEditModalOpened] = useState<boolean>(false);

  const canSeeOrganizations = rbacCheck(userData.roles, '/organizations:VIEW_ALL');
  const canSeeBusinessUnit = rbacCheck(userData.roles, '/business-units:VIEW', {
    businessUnit: projectDetails.businessUnit,
    userId: userData._id,
  });
  const canDeleteProject = rbacCheck(userData.roles, '/projects:DELETE');
  const canEditProjects = rbacCheck(userData.roles, '/projects:PATCH');

  const { makeApiCall: getProjectDetailsCall } = useApiQuery(
    Auth.fetchV2ProjectById as unknown as APIHookArg
  );
  const { makeApiCall: deleteProjectCall } = useApiQuery(
    Auth.deleteV2Project as unknown as APIHookArg,
    {
      successMessage: 'projectDeleted',
    }
  );

  const getProjectDetails = async (): Promise<void> => {
    const data = (await getProjectDetailsCall(projectId)) as ProjectDetails;
    setProjectDetails(data);
  };

  const deleteProject = async (): Promise<void> => {
    setDeleteModalOpened(false);

    try {
      const data = await deleteProjectCall(projectId);

      if (data) {
        navigate(`/governance/organizations/${projectDetails.organizationId}/projects`);
      }
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const getProjectInfo = (): ProjectInfoItem[] => [
    {
      title: 'Description',
      value: projectDetails.description,
    },
    {
      title: 'Organization',
      component: (
        <Link to={`/governance/organizations/${projectDetails.organizationId}`}>
          {projectDetails.organization?.enterpriseName}
        </Link>
      ),
    },
    {
      title: 'Business unit',
      component: canSeeBusinessUnit ? (
        <Link to={`/business-units/${projectDetails.businessUnitId}`}>
          {projectDetails.businessUnit?.name}
        </Link>
      ) : (
        projectDetails.businessUnit?.name
      ),
    },
    {
      title: 'Technical lead',
      value: projectDetails.technicalLead?.email ?? '',
      component: (
        <Link to={`/users/${projectDetails.technicalLead?.uuid ?? ''}`}>
          {projectDetails.technicalLead?.email}
        </Link>
      ),
    },
  ];

  return (
    <>
      {editModalOpened && (
        <EditProjectFormModal
          project={projectDetails}
          onSubmit={() => {
            getProjectDetails().catch(console.error);
          }}
          onCancel={() => {
            setEditModalOpened(false);
          }}
        />
      )}
      {deleteModalOpened && (
        <DeleteFormModal
          fieldName='Project'
          fieldValue={projectDetails.name}
          onSubmit={() => {
            deleteProject().catch(console.error);
          }}
          onCancel={() => {
            setDeleteModalOpened(false);
          }}
        />
      )}
      <Grid
        container
        className='content-with-header'
        direction='column'
      >
        <Grid item>
          <BreadCrumb
            items={[
              {
                text: 'organizations',
                href: canSeeOrganizations
                  ? '/governance/organizations'
                  : `/governance/organizations/${projectDetails.organizationId}`,
              },
              {
                text: projectDetails.organization?.enterpriseName ?? '',
                href: `/governance/organizations/${projectDetails.organizationId}`,
              },
              {
                text: 'projects',
                href: `/governance/organizations/${projectDetails.organizationId}/projects`,
              },
              {
                href: '#',
                text: projectDetails.name,
              },
            ]}
            onItemClicked={(event, clicked) => {
              event.preventDefault();

              const mapping: Record<'organizations' | 'org-name' | 'projects', string> = {
                organizations: canSeeOrganizations
                  ? '/governance/organizations'
                  : `/governance/organizations/${projectDetails.organizationId}`,
                'org-name': `/governance/organizations/${projectDetails.organizationId}`,
                projects: `/governance/organizations/${projectDetails.organizationId}/projects`,
              };
              const targetId = clicked.id as keyof typeof mapping;
              const path = mapping[targetId] || null;
              if (path) navigate(path);
            }}
          />
        </Grid>
        <Grid item>
          <Grid
            container
            direction='column'
            rowGap={8}
          >
            <Grid item>
              <OverviewTable
                header={projectDetails.name}
                subHeader='Add new projects, edit their details or select a project to configure its products.'
                rows={getProjectInfo()}
                editAction={{
                  onClick: () => {
                    setEditModalOpened(true);
                  },
                  disabled: !canEditProjects,
                  tooltip: 'editProjectDisabled',
                  label: '',
                }}
                deleteAction={{
                  onClick: () => {
                    setDeleteModalOpened(true);
                  },
                  disabled: !canDeleteProject,
                  tooltip: 'businessUnitDeletePermissionErr',
                  label: '',
                }}
              />
            </Grid>
            <Grid item>
              <Products orgId={projectDetails.organizationId} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default Project;
